import { useState, useMemo } from "react";
import { GroupedItem } from "@/lib/utils/crm/grouping-strategies";

/**
 * Hook to manage group expansion state
 * Handles both primary and secondary group expansion
 */
export function useGroupingState(
  groupByField: string | null,
  secondaryGroupByField: string | null | undefined,
  groupedData: any[]
) {
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [expandedSecondaryGroups, setExpandedSecondaryGroups] = useState<
    Map<string, Set<string>>
  >(new Map());

  // Auto-expand all primary groups on first grouping
  useMemo(() => {
    if (groupByField && expandedGroups.size === 0) {
      const allKeys = new Set<string>();
      (groupedData as GroupedItem[]).forEach((item: GroupedItem) => {
        if (item.type === "group-header" && item.groupKey) {
          allKeys.add(item.groupKey);
        }
      });
      if (allKeys.size > 0) {
        setExpandedGroups(allKeys);
      }
    }
  }, [groupByField, groupedData, expandedGroups.size]);

  // Auto-expand all secondary groups
  useMemo(() => {
    if (secondaryGroupByField && expandedSecondaryGroups.size === 0) {
      const newMap = new Map<string, Set<string>>();
      (groupedData as GroupedItem[]).forEach((item: GroupedItem) => {
        if (item.type === "group-header" && item.groupKeys && item.groupKeys.length === 2) {
          const [primaryKey, secondaryKey] = item.groupKeys as [string, string];
          if (!newMap.has(primaryKey)) {
            newMap.set(primaryKey, new Set());
          }
          newMap.get(primaryKey)!.add(secondaryKey);
        }
      });
      if (newMap.size > 0) {
        setExpandedSecondaryGroups(newMap);
      }
    }
  }, [secondaryGroupByField, groupedData, expandedSecondaryGroups.size]);

  /**
   * Toggle primary group expansion
   */
  const togglePrimaryGroup = (groupKey: string) => {
    setExpandedGroups((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(groupKey)) {
        newSet.delete(groupKey);
      } else {
        newSet.add(groupKey);
      }
      return newSet;
    });
  };

  /**
   * Toggle secondary group expansion
   */
  const toggleSecondaryGroup = (parentKey: string, groupKey: string) => {
    setExpandedSecondaryGroups((prev) => {
      const newMap = new Map(prev);
      const secondarySet = newMap.get(parentKey) || new Set();
      const newSet = new Set(secondarySet);
      if (newSet.has(groupKey)) {
        newSet.delete(groupKey);
      } else {
        newSet.add(groupKey);
      }
      newMap.set(parentKey, newSet);
      return newMap;
    });
  };

  /**
   * Check if a group is expanded
   */
  const isGroupExpanded = (groupKey: string, parentKey?: string): boolean => {
    if (parentKey) {
      return expandedSecondaryGroups.get(parentKey)?.has(groupKey) || false;
    }
    return expandedGroups.has(groupKey);
  };

  return {
    expandedGroups,
    expandedSecondaryGroups,
    togglePrimaryGroup,
    toggleSecondaryGroup,
    isGroupExpanded,
  };
}

// Made with Bob
